import React, { useEffect, useState } from 'react';
import { useMsal } from '@azure/msal-react';
import { authenticatedApiCall } from '../../utils/apiClient';

const compactCode = (value) => String(value || '').replace(/[-\s]/g, '').toUpperCase();

const overlay = {
  position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
  backgroundColor: 'rgba(0,0,0,0.55)', display: 'flex',
  justifyContent: 'center', alignItems: 'center', zIndex: 1100,
};
const box = {
  backgroundColor: '#fff', borderRadius: 10, padding: '1.5rem 1.75rem',
  width: '92%', maxWidth: 560, maxHeight: '85vh', overflowY: 'auto',
  boxShadow: '0 8px 28px rgba(0,0,0,0.25)',
};
const input = {
  width: '100%', padding: '0.65rem 0.85rem', border: '1px solid #ced4da',
  borderRadius: 6, fontSize: '0.95rem', boxSizing: 'border-box',
};
const warn = {
  padding: '0.85rem 1rem', backgroundColor: '#fff3cd', color: '#664d03',
  border: '1px solid #ffecb5', borderRadius: 6, fontSize: '0.9rem', lineHeight: 1.45,
  marginBottom: '1rem',
};
const errBox = {
  padding: '0.75rem 1rem', backgroundColor: '#f8d7da', color: '#721c24',
  borderRadius: 6, marginBottom: '0.75rem', border: '1px solid #f5c6cb', fontSize: '0.9rem',
};

export default function ProjectIdentityActions({ project, isSuperadmin, onChanged }) {
  const { instance, accounts } = useMsal();
  const [relinkOpen, setRelinkOpen] = useState(false);
  const [renameOpen, setRenameOpen] = useState(false);

  if (!isSuperadmin) return null;

  return (
    <>
      <div style={{
        marginTop: '1.25rem', padding: '1rem', border: '1px solid #f5c6cb',
        backgroundColor: '#fff8f8', borderRadius: 8,
      }}>
        <div style={{ fontWeight: 700, color: '#842029', marginBottom: 6 }}>Superadmin corrections</div>
        <p style={{ margin: '0 0 0.75rem', fontSize: '0.85rem', color: '#6c757d', lineHeight: 1.45 }}>
          Use these only when the award was typed against the wrong bid, or the job number itself is wrong and the job is still empty. Everyone assigned to the job stays assigned.
        </p>
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
          <button
            type="button"
            onClick={() => setRelinkOpen(true)}
            style={{ padding: '0.45rem 0.85rem', backgroundColor: '#b02a37', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: '0.85rem', fontWeight: 600 }}
          >
            Change linked estimate
          </button>
          <button
            type="button"
            onClick={() => setRenameOpen(true)}
            style={{ padding: '0.45rem 0.85rem', backgroundColor: '#6c757d', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: '0.85rem', fontWeight: 600 }}
          >
            Change job number
          </button>
        </div>
      </div>

      {relinkOpen && (
        <RelinkModal
          project={project}
          instance={instance}
          accounts={accounts}
          onClose={() => setRelinkOpen(false)}
          onChanged={() => { setRelinkOpen(false); onChanged && onChanged(); }}
        />
      )}
      {renameOpen && (
        <RenameModal
          project={project}
          instance={instance}
          accounts={accounts}
          onClose={() => setRenameOpen(false)}
          onChanged={() => { setRenameOpen(false); onChanged && onChanged(); }}
        />
      )}
    </>
  );
}

function RelinkModal({ project, instance, accounts, onClose, onChanged }) {
  const [query, setQuery] = useState('');
  const [hits, setHits] = useState([]);
  const [picked, setPicked] = useState(null);
  const [confirmCode, setConfirmCode] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    const q = query.trim();
    if (q.length < 2) { setHits([]); return undefined; }
    const t = setTimeout(async () => {
      try {
        const res = await authenticatedApiCall(instance, accounts, 'GET', `/estimates/search?q=${encodeURIComponent(q)}&limit=12`);
        if (!cancelled) setHits(res.data.data || []);
      } catch (e) {
        if (!cancelled) setHits([]);
      }
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
  }, [query, instance, accounts]);

  const handleSave = async () => {
    if (!picked) { setError('Pick the estimate this job should belong to.'); return; }
    setSaving(true);
    setError('');
    try {
      await authenticatedApiCall(instance, accounts, 'POST', `/projects/${project.id}/relink-estimate`, {
        estimate_number: picked.estimate_number,
        confirm_estimate_number: confirmCode.trim(),
        confirm: true,
      });
      onChanged();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to change the linked estimate');
    } finally {
      setSaving(false);
    }
  };

  const confirmOk = picked && compactCode(confirmCode) === compactCode(picked.estimate_number);

  return (
    <div style={overlay}>
      <div style={box}>
        <h3 style={{ margin: '0 0 0.75rem', color: '#842029' }}>Change linked estimate</h3>
        <div style={warn}>
          Job <strong>{project.job_number}</strong> stays. People assigned to the job stay. This only moves the award from{' '}
          <strong>{project.estimate_number || '(none)'}</strong> onto another estimate. Do this when the job number is right and the bid was wrong.
        </div>
        <label style={{ display: 'block', fontWeight: 600, marginBottom: 6, fontSize: '0.9rem' }}>Search estimates</label>
        <input
          style={input}
          value={query}
          onChange={(e) => { setQuery(e.target.value); setPicked(null); }}
          placeholder="26-FL-1023 or 26FL1023 or the client name"
        />
        <div style={{ border: '1px solid #e9ecef', borderRadius: 6, marginTop: 8, maxHeight: 220, overflowY: 'auto' }}>
          {hits.length === 0 ? (
            <div style={{ padding: '0.75rem', color: '#6c757d', fontSize: '0.85rem' }}>
              {query.trim().length < 2 ? 'Type at least two characters.' : 'No matching estimates.'}
            </div>
          ) : hits.map((row) => (
            <button
              key={row.id}
              type="button"
              onClick={() => { setPicked(row); setConfirmCode(''); }}
              style={{
                display: 'block', width: '100%', textAlign: 'left', border: 'none',
                borderBottom: '1px solid #f1f3f5', padding: '0.6rem 0.75rem', cursor: 'pointer',
                backgroundColor: picked?.id === row.id ? '#e7f3ff' : '#fff',
              }}
            >
              <div style={{ fontWeight: 600 }}>{row.estimate_number} — {row.estimate_name}</div>
              <div style={{ fontSize: '0.8rem', color: '#6c757d' }}>
                {row.client_name || 'No client'}{row.awarded ? ` · already awarded${row.job_number ? ` to ${row.job_number}` : ''}` : ''}
              </div>
            </button>
          ))}
        </div>
        {picked && (
          <div style={{ marginTop: '1rem' }}>
            <p style={{ margin: '0 0 0.5rem', fontSize: '0.9rem' }}>
              You are linking job <strong>{project.job_number}</strong> to{' '}
              <strong>{picked.estimate_number}</strong> — {picked.estimate_name}
              {picked.client_name ? ` (${picked.client_name})` : ''}.
            </p>
            <label style={{ display: 'block', fontWeight: 600, marginBottom: 6, fontSize: '0.9rem' }}>
              Type {picked.estimate_number} to confirm
            </label>
            <input style={input} value={confirmCode} onChange={(e) => setConfirmCode(e.target.value)} />
          </div>
        )}
        {error && <div style={{ ...errBox, marginTop: '0.75rem' }}>{error}</div>}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: '1.25rem' }}>
          <button type="button" onClick={onClose} disabled={saving} style={{ padding: '0.5rem 1rem', background: '#6c757d', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer' }}>Cancel</button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving || !confirmOk}
            style={{ padding: '0.5rem 1rem', background: confirmOk ? '#b02a37' : '#adb5bd', color: '#fff', border: 'none', borderRadius: 6, cursor: confirmOk ? 'pointer' : 'not-allowed', fontWeight: 600 }}
          >
            {saving ? 'Saving...' : 'Move the award'}
          </button>
        </div>
      </div>
    </div>
  );
}

function RenameModal({ project, instance, accounts, onClose, onChanged }) {
  const [scan, setScan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [newNumber, setNewNumber] = useState('');
  const [confirmNumber, setConfirmNumber] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [errorScan, setErrorScan] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await authenticatedApiCall(instance, accounts, 'GET', `/projects/${project.id}/identity-scan`);
        if (!cancelled) setScan(res.data.scan);
      } catch (err) {
        if (!cancelled) setError(err.response?.data?.message || 'Could not check whether this job is empty');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [project.id, instance, accounts]);

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setErrorScan(null);
    try {
      await authenticatedApiCall(instance, accounts, 'POST', `/projects/${project.id}/rename-job-number`, {
        new_job_number: newNumber.trim(),
        confirm_job_number: confirmNumber.trim(),
      });
      onChanged();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to change the job number');
      setErrorScan(err.response?.data?.scan || null);
    } finally {
      setSaving(false);
    }
  };

  const empty = scan?.empty === true;
  const confirmOk = empty && newNumber.trim() && newNumber.trim() === confirmNumber.trim() && newNumber.trim() !== project.job_number;
  const blocking = (errorScan?.blocking || scan?.blocking || []);

  return (
    <div style={overlay}>
      <div style={box}>
        <h3 style={{ margin: '0 0 0.75rem', color: '#842029' }}>Change job number</h3>
        <div style={warn}>
          This is the dangerous one. It is allowed only while job <strong>{project.job_number}</strong> has no tracker, time, MTO, or other working data. If anything else is already stored against this number, stop and ask someone who can clean the database first.
        </div>
        {loading ? (
          <p style={{ color: '#6c757d' }}>Checking what is stored against this job...</p>
        ) : empty ? (
          <p style={{ color: '#0f5132', background: '#d1e7dd', padding: '0.75rem 1rem', borderRadius: 6 }}>
            This job is still empty. People assigned to it will stay assigned after the number changes.
          </p>
        ) : (
          <div>
            <p style={{ color: '#842029', fontWeight: 600 }}>This job already has data. The number cannot be changed from the portal.</p>
            <ul style={{ fontSize: '0.85rem', color: '#495057' }}>
              {blocking.map((row) => (
                <li key={`${row.table}-${row.column}`}>{row.table} ({row.rows} rows)</li>
              ))}
            </ul>
          </div>
        )}
        {empty && (
          <>
            <label style={{ display: 'block', fontWeight: 600, margin: '0.75rem 0 6px', fontSize: '0.9rem' }}>New job number</label>
            <input style={input} value={newNumber} onChange={(e) => setNewNumber(e.target.value)} placeholder="The correct job number" />
            <label style={{ display: 'block', fontWeight: 600, margin: '0.75rem 0 6px', fontSize: '0.9rem' }}>Type it again to confirm</label>
            <input style={input} value={confirmNumber} onChange={(e) => setConfirmNumber(e.target.value)} />
          </>
        )}
        {error && <div style={{ ...errBox, marginTop: '0.75rem' }}>{error}</div>}
        {errorScan?.blocking?.length > 0 && (
          <ul style={{ fontSize: '0.85rem' }}>
            {errorScan.blocking.map((row) => (
              <li key={`${row.table}-${row.column}`}>{row.table} ({row.rows} rows)</li>
            ))}
          </ul>
        )}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: '1.25rem' }}>
          <button type="button" onClick={onClose} disabled={saving} style={{ padding: '0.5rem 1rem', background: '#6c757d', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer' }}>Cancel</button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving || !confirmOk}
            style={{ padding: '0.5rem 1rem', background: confirmOk ? '#b02a37' : '#adb5bd', color: '#fff', border: 'none', borderRadius: 6, cursor: confirmOk ? 'pointer' : 'not-allowed', fontWeight: 600 }}
          >
            {saving ? 'Saving...' : 'Change the job number'}
          </button>
        </div>
      </div>
    </div>
  );
}
